import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { SxProps, Theme } from '@mui/material/styles'
import ScrollHint from 'scroll-hint'

const DRAG_THRESHOLD = 5
const SCROLL_HINT_SELECTOR = '.js-scrollable-finalize'

/** Content area inside the box at default desktop width. */
export const FINALIZE_DEFAULT_CONTENT_WIDTH = 1200
/** Default outer box width: 1200 content + paper horizontal padding on md. */
export const FINALIZE_DEFAULT_BOX_WIDTH = 1280
/** Reserved space from scrollbarGutter: stable on TableContainer. */
export const FINALIZE_TABLE_SCROLLBAR_GUTTER = 12

const DRAWER_WIDTH_OPEN = 264
const DRAWER_WIDTH_CLOSED = 64

/** Matches styles.paper: p: { xs: '20px', sm: '30px', md: 5 } */
export const getFinalizePaperHorizontalPadding = (viewportWidth: number) => {
    if (viewportWidth < 600) {
        return 40
    }
    if (viewportWidth < 900) {
        return 60
    }
    return 80
}

const getMainHorizontalPadding = (viewportWidth: number) => {
    if (viewportWidth < 600) {
        return 20
    }
    if (viewportWidth < 900) {
        return 30
    }
    return 24
}

export const getFinalizeDefaultTableAreaWidth = () => {
    return FINALIZE_DEFAULT_CONTENT_WIDTH
}

export const estimateFinalizeContentAreaWidth = (
    viewportWidth: number,
    sidebarOpen: boolean,
) => {
    const mainPaddingX = getMainHorizontalPadding(viewportWidth) * 2
    const sidebarMl = viewportWidth >= 900 ? 10 : 0
    const sidebarWidth =
        viewportWidth >= 900 ? (sidebarOpen ? DRAWER_WIDTH_OPEN : DRAWER_WIDTH_CLOSED) : 0

    return Math.max(0, viewportWidth - sidebarWidth - mainPaddingX - sidebarMl)
}

const getMainContentAreaElement = (block: HTMLElement | null) => {
    return block?.closest('main') ?? null
}

/** Width inside <main> padding — fixed by flex layout, not inflated by wide table children. */
export const readFinalizeContentAreaWidth = (
    block: HTMLElement | null,
    viewportWidth: number,
    sidebarOpen: boolean,
) => {
    const main = getMainContentAreaElement(block)
    if (main && main.clientWidth > 0) {
        const style = window.getComputedStyle(main)
        const paddingX =
            Number.parseFloat(style.paddingLeft) + Number.parseFloat(style.paddingRight)
        return Math.max(0, main.clientWidth - paddingX)
    }

    return estimateFinalizeContentAreaWidth(viewportWidth, sidebarOpen)
}

export const getFinalizeMaxBoxWidth = (contentAreaWidth: number) => {
    return Math.max(0, contentAreaWidth)
}

export type FinalizeLayoutState = {
    needsWidenBlock: boolean
    expandedBoxWidth: number | null
    needsTableScroll: boolean
}

const clampBoxWidth = (desiredBoxWidth: number, maxBoxWidth: number) => {
    const minBoxWidth = Math.min(FINALIZE_DEFAULT_BOX_WIDTH, maxBoxWidth)
    return Math.min(maxBoxWidth, Math.max(minBoxWidth, desiredBoxWidth))
}

export const computeFinalizeLayout = (
    naturalTableWidth: number,
    viewportWidth: number,
    contentAreaWidth: number,
): FinalizeLayoutState => {
    const paperPadding = getFinalizePaperHorizontalPadding(viewportWidth)
    const maxBoxWidth = getFinalizeMaxBoxWidth(contentAreaWidth)

    let desiredBoxWidth = naturalTableWidth + paperPadding
    let expandedBoxWidth = clampBoxWidth(desiredBoxWidth, maxBoxWidth)
    let availableTableWidth = expandedBoxWidth - paperPadding

    if (naturalTableWidth > availableTableWidth) {
        desiredBoxWidth = naturalTableWidth + paperPadding + FINALIZE_TABLE_SCROLLBAR_GUTTER
        expandedBoxWidth = clampBoxWidth(desiredBoxWidth, maxBoxWidth)
        availableTableWidth = expandedBoxWidth - paperPadding
    }

    const needsWidenBlock = expandedBoxWidth > FINALIZE_DEFAULT_BOX_WIDTH
    const needsTableScroll = naturalTableWidth > availableTableWidth

    return {
        needsWidenBlock,
        expandedBoxWidth: needsWidenBlock ? expandedBoxWidth : null,
        needsTableScroll,
    }
}

export const getFinalizeTargetBoxWidth = (
    tableWidth: number,
    viewportWidth: number,
    contentAreaWidth: number,
) => {
    const layout = computeFinalizeLayout(tableWidth, viewportWidth, contentAreaWidth)
    return layout.expandedBoxWidth ?? Math.min(FINALIZE_DEFAULT_BOX_WIDTH, getFinalizeMaxBoxWidth(contentAreaWidth))
}

const measureNaturalTableWidth = (table: HTMLTableElement) => {
    const savedWidth = table.style.width
    const savedTableLayout = table.style.tableLayout
    const savedMinWidth = table.style.minWidth

    table.style.width = 'max-content'
    table.style.tableLayout = 'auto'
    table.style.minWidth = 'max-content'
    void table.offsetWidth

    const naturalTableWidth = table.scrollWidth

    table.style.width = savedWidth
    table.style.tableLayout = savedTableLayout
    table.style.minWidth = savedMinWidth

    return naturalTableWidth
}

export const getFinalizeContainerSx = (
    needsWidenBlock: boolean,
    expandedBoxWidth: number | null,
    contentAreaWidth: number,
): SxProps<Theme> => {
    const maxBoxWidth = getFinalizeMaxBoxWidth(contentAreaWidth)

    if (!needsWidenBlock || expandedBoxWidth === null) {
        return {
            width: '100%',
            maxWidth: `${Math.min(FINALIZE_DEFAULT_BOX_WIDTH, maxBoxWidth)}px`,
            minWidth: '0',
            mx: 'auto',
            boxSizing: 'border-box',
        }
    }

    return {
        width: '100%',
        maxWidth: `${Math.min(expandedBoxWidth, maxBoxWidth)}px`,
        minWidth: '0',
        mx: 'auto',
        px: 0,
        boxSizing: 'border-box',
    }
}

export const getFinalizeExpandedPaperSx = (shouldExpandLayout: boolean) => {
    if (!shouldExpandLayout) {
        return {}
    }

    return {
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        alignItems: 'stretch !important',
        alignSelf: 'stretch',
        boxSizing: 'border-box',
        overflowX: 'hidden',
    }
}

export const getFinalizeExpandedFormSx = (shouldExpandLayout: boolean) => {
    if (!shouldExpandLayout) {
        return {}
    }

    return {
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        margin: 0,
        alignSelf: 'stretch !important',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        boxSizing: 'border-box',
        overflowX: 'hidden',
    }
}

export const finalizeWideTableSx = {
    width: 'max-content',
    minWidth: '100%',
} as const

export const finalizeHorizontalTableScrollableSx = {
    width: '100%',
    maxWidth: '100%',
    overflowX: 'scroll',
    overflowY: 'hidden',
    justifyContent: 'flex-start',
    scrollbarGutter: 'stable',
    WebkitOverflowScrolling: 'touch',
    '::-webkit-scrollbar': {
        display: 'block',
        height: 10,
    },
    '::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(0, 0, 0, 0.35)',
        borderRadius: 5,
    },
    '::-webkit-scrollbar-track': {
        backgroundColor: 'rgba(0, 0, 0, 0.08)',
    },
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(0, 0, 0, 0.35) rgba(0, 0, 0, 0.08)',
    MsOverflowStyle: 'auto',
} as const

type UseFinalizeHorizontalTableScrollOptions = {
    viewportWidth: number
    sidebarOpen?: boolean
    deps?: unknown[]
}

const layoutStatesEqual = (a: FinalizeLayoutState, b: FinalizeLayoutState) => {
    return (
        a.needsWidenBlock === b.needsWidenBlock &&
        a.expandedBoxWidth === b.expandedBoxWidth &&
        a.needsTableScroll === b.needsTableScroll
    )
}

export function useFinalizeHorizontalTableScroll({
    viewportWidth,
    sidebarOpen = true,
    deps = [],
}: UseFinalizeHorizontalTableScrollOptions) {
    const containerRef = useRef<HTMLDivElement>(null)
    const tableRef = useRef<HTMLTableElement>(null)
    const blockRef = useRef<HTMLDivElement>(null)
    const contentAreaWidthRef = useRef(0)
    const [needsWidenBlock, setNeedsWidenBlock] = useState(false)
    const [expandedBoxWidth, setExpandedBoxWidth] = useState<number | null>(null)
    const [needsTableScroll, setNeedsTableScroll] = useState(false)
    const [contentAreaWidth, setContentAreaWidth] = useState(() =>
        estimateFinalizeContentAreaWidth(viewportWidth, sidebarOpen),
    )
    const [isDragging, setIsDragging] = useState(false)
    const dragState = useRef({
        startX: 0,
        scrollLeft: 0,
        moved: false,
        active: false,
    })
    const justDraggedRef = useRef(false)
    const layoutStateRef = useRef<FinalizeLayoutState>({
        needsWidenBlock: false,
        expandedBoxWidth: null,
        needsTableScroll: false,
    })

    const clampTableScroll = useCallback((nextContentAreaWidth: number) => {
        const tableContainer = containerRef.current
        if (!tableContainer) {
            return
        }

        const maxScrollLeft = Math.max(0, tableContainer.scrollWidth - tableContainer.clientWidth)
        if (tableContainer.scrollLeft > maxScrollLeft) {
            tableContainer.scrollLeft = maxScrollLeft
        }

        if (nextContentAreaWidth < contentAreaWidthRef.current) {
            tableContainer.scrollLeft = Math.min(tableContainer.scrollLeft, maxScrollLeft)
        }
    }, [])

    const readContentAreaWidth = useCallback(() => {
        return readFinalizeContentAreaWidth(blockRef.current, viewportWidth, sidebarOpen)
    }, [sidebarOpen, viewportWidth])

    const applyLayoutState = useCallback((next: FinalizeLayoutState) => {
        if (layoutStatesEqual(layoutStateRef.current, next)) {
            return
        }

        layoutStateRef.current = next
        setNeedsWidenBlock(next.needsWidenBlock)
        setExpandedBoxWidth(next.expandedBoxWidth)
        setNeedsTableScroll(next.needsTableScroll)
    }, [])

    const measureOverflow = useCallback(() => {
        const table = tableRef.current
        const measuredContentAreaWidth = readContentAreaWidth()

        if (measuredContentAreaWidth !== contentAreaWidthRef.current) {
            clampTableScroll(measuredContentAreaWidth)
            contentAreaWidthRef.current = measuredContentAreaWidth
            setContentAreaWidth(measuredContentAreaWidth)
        }

        if (!table) {
            applyLayoutState({
                needsWidenBlock: false,
                expandedBoxWidth: null,
                needsTableScroll: false,
            })
            return
        }

        const naturalTableWidth = measureNaturalTableWidth(table)
        applyLayoutState(
            computeFinalizeLayout(naturalTableWidth, viewportWidth, measuredContentAreaWidth),
        )
    }, [applyLayoutState, clampTableScroll, readContentAreaWidth, viewportWidth])

    useLayoutEffect(() => {
        measureOverflow()

        const resizeObserver = new ResizeObserver(measureOverflow)
        if (tableRef.current) {
            resizeObserver.observe(tableRef.current)
        }

        const contentMain = getMainContentAreaElement(blockRef.current)
        if (contentMain) {
            resizeObserver.observe(contentMain)
        }

        window.addEventListener('resize', measureOverflow)

        return () => {
            resizeObserver.disconnect()
            window.removeEventListener('resize', measureOverflow)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [measureOverflow, viewportWidth, sidebarOpen, ...deps])

    useLayoutEffect(() => {
        const tableContainer = containerRef.current
        if (!tableContainer) {
            return
        }

        if (!needsTableScroll) {
            tableContainer.style.overflowX = ''
            return
        }

        // if (!document.querySelector(`${SCROLL_HINT_SELECTOR} [data-target="scrollable-icon"]`)) {
        //     new ScrollHint(SCROLL_HINT_SELECTOR, {
        //         i18n: {
        //             scrollable: 'スクロールできます',
        //         },
        //     })
        // }

        tableContainer.style.overflowX = 'scroll'
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [needsTableScroll, viewportWidth, sidebarOpen, ...deps])

    const handleMouseDown = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            if (!needsTableScroll || !containerRef.current || event.button !== 0) {
                return
            }

            const target = event.target as HTMLElement
            if (target.closest('input, textarea, button, a, select, [contenteditable="true"]')) {
                return
            }

            dragState.current = {
                startX: event.clientX,
                scrollLeft: containerRef.current.scrollLeft,
                moved: false,
                active: true,
            }
        },
        [needsTableScroll],
    )

    useEffect(() => {
        if (!needsTableScroll) {
            return
        }

        const handleMouseMove = (event: MouseEvent) => {
            const tableContainer = containerRef.current
            if (!tableContainer || !dragState.current.active) {
                return
            }

            const deltaX = event.clientX - dragState.current.startX
            if (!dragState.current.moved && Math.abs(deltaX) > DRAG_THRESHOLD) {
                dragState.current.moved = true
                setIsDragging(true)
            }

            if (dragState.current.moved) {
                event.preventDefault()
                tableContainer.scrollLeft = dragState.current.scrollLeft - deltaX
            }
        }

        const endDrag = () => {
            if (!dragState.current.active) {
                return
            }

            if (dragState.current.moved) {
                justDraggedRef.current = true
            }

            dragState.current = {
                startX: 0,
                scrollLeft: 0,
                moved: false,
                active: false,
            }
            setIsDragging(false)
        }

        window.addEventListener('mousemove', handleMouseMove)
        window.addEventListener('mouseup', endDrag)

        return () => {
            window.removeEventListener('mousemove', handleMouseMove)
            window.removeEventListener('mouseup', endDrag)
        }
    }, [needsTableScroll])

    const guardClick = useCallback((callback: () => void) => {
        if (justDraggedRef.current) {
            justDraggedRef.current = false
            return
        }
        callback()
    }, [])

    return {
        blockRef,
        containerRef,
        tableRef,
        contentAreaWidth,
        needsWidenBlock,
        expandedBoxWidth,
        needsTableScroll,
        isDragging,
        handleMouseDown,
        guardClick,
    }
}
